// 批次 43（#41 GNL-005-e，issue #41）：共同編輯表單填寫（form-co-edit-form-fill-form）整頁 body。
//
// origin：WEB-INF/views/form/coEditForm/fillForm.jsp（944 行；prompt decorator 頁——URL 無
// formKey 參數時表單資訊全空、form 內容為 server 動態產生）。complexBody hook（批次 16/41 先例）。
//
// 【為什麼整頁自訂】泛用 ComplexFormPage 是「單筆編輯表單」模板（儲存/取消＋h3 基本資料）；
// legacy 此頁是「表單資訊面板＋動態填寫表單（form#formForm action=coEditForm/submit）＋
// 關閉/列印」——結構完全不同；react 現況的 hidden inputs（action/formKey/reason/…）其實正確，
// 但 form 缺 action/method、標題/按鈕全錯位。
//
// 【鏡像範圍（fillForm.jsp 行號＋raw-dom 親讀 2026-10-02；無 formKey 捕捉態＝值全空）】
//   - 表單資訊 ibox（:715-765）：h5 表單資訊＋課程日期：/訓練科部：/表單名稱： 三列（值列
//     捕捉態為空＝照實空值【雛形值待實機校準】；主筆人員/協同人員等 flow 列＝versionFlowListMap
//     相依＝捕捉態無此列，不鏡像）。
//   - h3>b（:772-774）：表單名稱（捕捉態空＝空 b）。
//   - form#formForm（:776-）：action="coEditForm/submit" method="POST" enctype multipart＋
//     契約 hidden 欄位（action/formKey/reason/rejectToFlowId/isEraseMe＝LegacyContractFields 照畫）；
//     動態表單內容區（genRecordForm server HTML）＝捕捉態為空、0 契約＝不鏡像（KNOWN-NOT-DONE）。
//   - 關閉/列印（:801/:803 input type=button value）：關閉＝window.close（雛形 navigate(-1)
//     誠實標註，B19-2 站內導航先例）；列印＝printForm()（:604-605 隱 qrcode 後 window.print）。
//   - form-img FAB（:739 區 a.form-img-fab javascript:void(0) icon-only）＝空 a 鏡像（href 屬性
//     差＝React 安全限制，批次 41 同款 AD）。
//   - form action="/nothing"（:299 同款 hidden carrier）＝S2-form AD 不鏡像（裁決②）。
//   - signature 範本（common/signature.jsp:41-42 div.hidden＋table {{template}}）＝範本 AD
//     不鏡像（裁決②）；formImgTableTrTemplate textarea（:511 附近）＝範本 AD 不鏡像。
//
// 【誠實標註】動態表單內容（server 產生欄位）未鏡像＝0 契約頁型（KNOWN-NOT-DONE 既有登記）；
//   關閉＝navigate(-1)（window.close 在 SPA 的近似）；表單資訊值列＝捕捉態空值。
//
// 【證據等級】§7 第 3 級（原始碼＋raw-dom 親讀 2026-10-02）。

import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { ContentPanel, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { legacyContractFieldsFor, seedValueFor, useDataSource } from '@/mocks';
import { legacyAlertFallback } from '../../templates/page-behaviors';

export function CoEditFillFormBody({ pageId }: { pageId: string }): ReactElement {
  const navigate = useNavigate();
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const contractFields = legacyContractFieldsFor(pageId) ?? [];
  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="表單資訊"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {/* 表單資訊列（:722-760）：label＋值列（捕捉態＝空值照實）。 */}
        <div data-ui-id={`${pageId}-info`} className="space-y-1">
          <div className="flex">
            <div className="w-24 text-right">課程日期：</div>
            <div className="flex-1 text-left" />
          </div>
          <div className="flex">
            <div className="w-24 text-right">訓練科部：</div>
            <div className="flex-1 text-left" />
          </div>
          <div className="flex">
            <div className="w-24 text-right">表單名稱：</div>
            <div className="flex-1 text-left" />
          </div>
        </div>
        {/* 表單名稱大標（:772-774 h3>b；捕捉態空值＝空 b 照實）。 */}
        <h3 className="text-center">
          <b />
        </h3>
        {/* form#formForm（:776-；action/method/enctype 鏡像；動態內容區＝捕捉態空）。 */}
        <form
          id="formForm"
          data-ui-id={`${pageId}-form`}
          action="coEditForm/submit"
          method="POST"
          encType="multipart/form-data"
          className="col-span-12"
          onSubmit={(event) => {
            event.preventDefault();
            // legacy onsubmit=checkSubmit()→POST coEditForm/submit；endpoint 未映射＝
            // update 嘗試誠實失敗（D21-4；payload 帶契約 seed 值＝C5-h 接線）。
            const payload: Record<string, string> = {};
            for (const field of contractFields) payload[field.name] = seedValueFor(field);
            void dialogs.processing
              .wrap(
                // prompt 頁無表列 id；以 '0' 嘗試（MockDataError＝誠實未映射登記）。
                dataSource.update(pageId, '0', payload),
              )
              .catch(async (cause: unknown) => {
                await dialogs.alert(legacyAlertFallback(cause));
              });
          }}
        >
          <input type="hidden" id="action" name="action" value="" />
          <input type="hidden" id="formKey" name="formKey" value="" />
          <input type="hidden" id="reason" name="reason" value="" />
          <input type="hidden" id="rejectReason" value="" />
          <input type="hidden" id="rejectToFlowId" name="rejectToFlowId" value="" />
          <input type="hidden" id="isEraseMe" name="isEraseMe" value="false" />
          {/* 送出鈕：legacy 的 submit 觸發器在動態內容（server 欄位）內＝捕捉態不顯現；
              雛形以隱藏送出鈕承接 C5-h 接線（legacy 無此元素＝1 筆多元素 AD 登記）。 */}
          <button type="submit" data-ui-id={`${pageId}-form-submit`} className="hidden">
            送出
          </button>
        </form>
        {/* 關閉/列印（:801/:803）。 */}
        <div data-ui-id={`${pageId}-actions`} className="flex flex-wrap items-center gap-2">
          <Button
            uiId={`${pageId}-btn-close`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              // :801 window.close()；SPA 以站內返回近似（B19-2 先例；誠實標註）。
              navigate(-1);
            }}
          >
            關閉
          </Button>
          <Button
            uiId={`${pageId}-btn-print`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              // :803 printForm()（:604-605 隱藏 qrcode/url-copy 後列印）。
              window.print();
            }}
          >
            列印
          </Button>
        </div>
      </ContentPanel>
      {/* form-img FAB（:739 區；a.form-img-fab icon-only；href=javascript:void(0) 屬性差＝AD）。 */}
      <a data-ui-id={`${pageId}-form-img-fab`} className="form-img" />
      <a data-ui-id={`${pageId}-form-comment-fab`} className="form-comment">
        <p />
      </a>
    </div>
  );
}
